import { clsx } from "clsx";
import type { ReactNode, RefObject } from "react";

// The one panel shell the Tech picker and the Loadout picker share (review 2026-09-08). The
// two controls are siblings in every way that matters — the same header bar with a summary
// on the left and quiet actions on the right, the same self-scrolling body — and until now
// each carried its own copy of the shell, which is the drift the shared SheetRow and Chip
// were each extracted to stop. Only the width differs, and the caller says which.

/** The panel, anchored to the control's LEFT edge at every width. Issue 24's narrower panel
 * hung off the control's right edge above sm; these carry Effect Lines and are wide enough
 * that the same anchoring put the Tech panel's left edge at -43 px on the unit page, whose
 * control sits 447 px in (measured 2026-09-06). Growing rightwards from the control clears
 * the viewport on every route at every width the 375 px to 1280 px sweep covers; each caller
 * then nudges the panel back on screen through `panelRef` where its own toolbar needs it. */
export function PickerPanel({
  panelRef,
  panelId,
  width,
  summary,
  actions,
  note,
  children,
}: {
  panelRef: RefObject<HTMLDivElement | null>;
  /** The panel's own DOM id, wired to its trigger's aria-controls. */
  panelId: string;
  /** The panel's width class — the one thing the two pickers differ on. */
  width: string;
  /** What the panel is holding, in the header's left corner. */
  summary: ReactNode;
  /** The header's quiet actions (PanelAction), right-aligned. */
  actions: ReactNode;
  /** A sentence between the header and the rows — what the last change did. */
  note?: string;
  children: ReactNode;
}) {
  return (
    <div
      ref={panelRef}
      id={panelId}
      className={clsx(
        "absolute left-0 z-30 mt-1 flex max-h-[70vh] flex-col overflow-hidden rounded border border-line-strong bg-raised shadow-lg",
        width,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-line-faint px-3 py-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted">
          {summary}
        </span>
        <span className="flex items-center gap-3">{actions}</span>
      </div>
      {note !== undefined && (
        <p className="border-b border-line-faint px-3 py-2 text-[12.5px] text-ink-2">{note}</p>
      )}
      {/* The panel scrolls within itself: at 375 px the page body must never scroll sideways,
          and a list this long must never push the page's own scroll height around. */}
      <div className="overflow-y-auto overscroll-contain px-3 py-2">{children}</div>
    </div>
  );
}

/** Nudge an open panel back inside the viewport, never past its own left margin. Both
 * pickers hang from a control whose left edge moves with the triggers beside it, so a
 * left-anchored panel can overhang the right edge at some widths: the Tech control on /ships
 * sits 909 px in at 1280 px and its 512 px panel reached 1421 px, giving the whole page a
 * sideways scroll (measured on the served build 2026-09-08). The Loadout picker had this
 * rule from Issue 46; the Tech picker only nudged inside the comparison's horizontal
 * scroller, so it is the one rule here now and each picker calls it on open. Reset first, so
 * a re-run measures the anchored position and not the last nudge. */
export function keepPanelOnScreen(element: HTMLElement): void {
  const margin = 8;
  element.style.left = "0px";
  const box = element.getBoundingClientRect();
  // The viewport WITHOUT the scrollbar: a panel sized from 100vw (which includes it) sat 7 px
  // past the right edge at 375 px and gave the page a sideways scroll (verified 2026-09-10 on
  // the three pickers); innerWidth would have called that on-screen.
  const overflow = box.right - (document.documentElement.clientWidth - margin);
  if (overflow > 0) element.style.left = `${-Math.min(overflow, box.left - margin)}px`;
}

/** One of the panel header's quiet actions. Never sets `outline-none`: that would defeat the
 * focus-visible ring the keyboard path depends on (a trap this repo has been bitten by). */
export function PanelAction({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer rounded font-mono text-[11px] uppercase tracking-[0.06em] text-muted-2 underline decoration-dotted underline-offset-2 hover:text-ink-3 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-link"
    >
      {children}
    </button>
  );
}

/** A row's footer: the game's own Effect Lines for the level in force, then the row's small
 * print (price, rules, what requires it). The game colours the lines green; we deliberately
 * do not (ADR 0005: order and grouping are mirrored, never how a value renders). */
export function RowFooter({
  effects,
  children,
}: {
  effects: readonly string[];
  children: ReactNode;
}) {
  return (
    <>
      {effects.length > 0 && (
        <span className="flex flex-col font-mono text-[11.5px] leading-snug text-ink-2">
          {effects.map((line) => (
            <span key={line}>{line}</span>
          ))}
        </span>
      )}
      <span className="flex flex-wrap gap-x-3 font-mono text-[10.5px] leading-snug text-muted-2">
        {children}
      </span>
    </>
  );
}
